import { Checkbox } from '@propertyfinder/checkbox';
import { FunctionalComponent, h } from 'preact';

import { configGetCountry } from 'common/helper/config/get-country';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { preactDynamicComponent } from 'common/module/preact/dynamic/component';
import { preactLifecycleComponent } from 'common/module/preact/lifecycle-component';
import { BuildingReviewAutocompleteTemplate } from 'desktop/module/building-review/autocomplete/template';
import { BuildingReviewFormRatingType } from 'desktop/module/building-review/form/types';

import { BuildingReviewFormTemplatePropsInterface } from './template-props.interface';

const LocationAutocomplete = preactDynamicComponent(BuildingReviewAutocompleteTemplate);
// tslint:enable-next-line:max-line-length
const termsText = i18nTranslate(`I agree to {link} of propertyfinder.`);
// tslint:enable-next-line:max-line-length
const termsTextParts = termsText.split('{link}');
const Markup: FunctionalComponent<BuildingReviewFormTemplatePropsInterface> = (props) => {
  const onChangeTerms = (e: Event): void => {
    props.onClickAcceptTerms();
    props.onChangeInput(e);
  };

  const isSubmitDisabled = (): boolean => {
    const isRatingsValid = !props.ratings.reduce(
      (acc: boolean, currentValue: BuildingReviewFormRatingType) => !props.input[currentValue].value || acc,
      false
    );
    const isFormValid =
      isRatingsValid &&
      !props.input.location.error &&
      props.input.location.value &&
      !props.input.reviewer_name.error &&
      props.input.reviewer_name.value &&
      !props.input.terms.error &&
      props.input.terms.value;

    return !isFormValid;
  };

  return (
    <div className='building-review-form'>
      <div className='modal_closebutton' onClick={props.onClickClose}>
        <span
          className='svg'
          dangerouslySetInnerHTML={{
            __html: `
          <svg viewBox='0 0 30 20' class='modal_close'>
            <use xlink:href='#svg-close'></use>
          </svg>
        `,
          }}
        />
      </div>
      {props.action === 'send-success' ? (
        <div className='building-review-form__confirm success-message'>
          <span
            className='svg'
            dangerouslySetInnerHTML={{
              __html: `
            <svg viewBox='0 0 32 32' class='success-message__icon'>
              <use xlink:href='#svg-success-icon'></use>
            </svg>
          `,
            }}
          />
          <div className='success-message__text'>
            <span>{i18nTranslate('Thank you')}!</span>
            <span className='success-message__text--building-review'>
              {i18nTranslate(
                'Your review has been submitted and is being verified by our team. ' + 'It should be online shortly.'
              )}
            </span>
          </div>
          <button className='button button--size5' onClick={props.onClickClose}>
            {i18nTranslate('Close')}
          </button>
        </div>
      ) : (
        <div className='building-review-form__field-set'>
          <h2 className='building-review-form__title'>
            {props.hasAutocomplete ? i18nTranslate('Select Building') : props.location.name}
            {!props.hasAutocomplete && (
              <div className='building-review-form__more-review' onClick={props.onClickSelectAnotherBuilding}>
                {i18nTranslate('Select another building')}
              </div>
            )}
          </h2>
          {props.hasAutocomplete && (
            <div className='building-review-form__autocomplete-area'>
              <LocationAutocomplete viewStore={props.autocompleteViewStore} />
              {props.input.location.error ? (
                <div className='building-review-form__error'>{props.input.location.error}</div>
              ) : (
                ''
              )}
            </div>
          )}
          <h3 className='building-review-form__sub-heading'>{i18nTranslate('Rate')}</h3>
          <ul className='building-review-form__ratings'>
            {props.ratings.map((name: BuildingReviewFormRatingType) => (
              <li className='building-review-form__rating'>
                <span className={'building-review-form__rating-label'}>{props.input[name].placeholder}</span>
                <div className='rating__stars rating__stars--size1'>
                  {props.stars.map((value: number) => (
                    <div className='rating__star-container'>
                      <span
                        className='svg'
                        dangerouslySetInnerHTML={{
                          __html: `
                          <svg viewBox='0 0 512 512'
                            class='rating__star rating__star--size1 ${
                              props.input[name].valueDisplayed >= value ? 'rating__star--active' : ''
                            }'
                          >
                            <use xlink:href='#svg-star'></use>
                          </svg>
                        `,
                        }}
                      />
                      {/* xlink:href makes DOM listeners buggy, so we use a mask instead */}
                      <div
                        className='rating__star-mask'
                        onMouseOver={(e) => {
                          e.preventDefault();
                          props.onMouseOverStars(name, value);
                        }}
                        onMouseLeave={(e) => {
                          e.preventDefault();
                          props.onMouseLeaveStars(name, value);
                        }}
                        onClick={(e) => props.onClickStars(name, value)}
                      />
                    </div>
                  ))}
                </div>
                <div className='building-review-form__tag'>
                  <div
                    className={`building-review-form__note ${
                      props.input[name].error
                        ? 'building-review-form__note--error'
                        : props.input[name].valueDisplayed === 0
                        ? 'building-review-form__note--active'
                        : ''
                    }`}
                  >
                    {i18nTranslate('click stars to rate')}
                  </div>
                  <div
                    className={`building-review-form__note building-review-form__note--red ${
                      !props.input[name].error && props.input[name].valueDisplayed === 1
                        ? 'building-review-form__note--active'
                        : ''
                    }`}
                  >
                    {i18nTranslate('Very Poor')}
                  </div>
                  <div
                    className={`building-review-form__note building-review-form__note--red ${
                      !props.input[name].error && props.input[name].valueDisplayed === 2
                        ? 'building-review-form__note--active'
                        : ''
                    }`}
                  >
                    {i18nTranslate('Poor')}
                  </div>
                  <div
                    className={`building-review-form__note building-review-form__note--green ${
                      !props.input[name].error && props.input[name].valueDisplayed === 3
                        ? 'building-review-form__note--active'
                        : ''
                    }`}
                  >
                    {i18nTranslate('OK')}
                  </div>
                  <div
                    className={`building-review-form__note building-review-form__note--green ${
                      !props.input[name].error && props.input[name].valueDisplayed === 4
                        ? 'building-review-form__note--active'
                        : ''
                    }`}
                  >
                    {i18nTranslate('Good')}
                  </div>
                  <div
                    className={`building-review-form__note building-review-form__note--green ${
                      !props.input[name].error && props.input[name].valueDisplayed === 5
                        ? 'building-review-form__note--active'
                        : ''
                    }`}
                  >
                    {i18nTranslate('Very Good')}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <h3 className='building-review-form__sub-heading'>{i18nTranslate('Write a review')}</h3>
          <div className='building-review-form__write'>
            <div className='building-review-form__control'>
              <input
                type='text'
                name='title'
                placeholder={props.input.title.placeholder}
                value={props.input.title.value}
                onChange={props.onChangeInput}
                onKeyUp={props.onKeyUpInput}
                className={`input building-review-form__input ${props.input.title.error ? 'input--invalid' : ''}`}
              />
              {props.input.title.error ? (
                <div className='building-review-form__error'>{props.input.title.error}</div>
              ) : (
                ''
              )}
            </div>
            <div className='building-review-form__control building-review-form__control-text'>
              <textarea
                name='text'
                placeholder={props.input.text.placeholder}
                onChange={props.onChangeInput}
                onInput={props.onInputText}
                className={`input building-review-form__input building-review-form__input-text ${
                  props.input.text.error ? 'input--invalid' : ''
                }`}
              >
                {props.input.text.value}
              </textarea>
              <span className={'building-review-form__counter'}>
                {props.input.text.currentLength}/{props.input.text.minLength}
              </span>
              {props.input.text.error ? (
                <div className='building-review-form__error'>{props.input.text.error}</div>
              ) : (
                ''
              )}
            </div>
          </div>
          <h3 className='building-review-form__sub-heading'>{i18nTranslate('Enter your details')}</h3>
          <div className={'building-review-form__user-detail'}>
            <div className={'building-review-form__detail'}>
              <div className={'building-review-form__control'}>
                <input
                  type='text'
                  name='reviewer_name'
                  placeholder={props.input.reviewer_name.placeholder}
                  value={props.input.reviewer_name.value}
                  onChange={props.onChangeInput}
                  onKeyUp={props.onKeyUpInput}
                  className={`input building-review-form__input ${
                    props.input.reviewer_name.error ? 'input--invalid' : ''
                  }`}
                />
                {props.input.reviewer_name.error ? (
                  <div className='building-review-form__error'>{props.input.reviewer_name.error}</div>
                ) : (
                  ''
                )}
              </div>
              <div className='building-review-form__control'>
                <input
                  type='text'
                  name='reviewer_email'
                  placeholder={props.input.reviewer_email.placeholder}
                  value={props.input.reviewer_email.value}
                  onChange={props.onChangeInput}
                  onKeyUp={props.onKeyUpInput}
                  className={`input building-review-form__input ${
                    props.input.reviewer_email.error ? 'input--invalid' : ''
                  }`}
                />
                {props.input.reviewer_email.error ? (
                  <div className='building-review-form__error'>{props.input.reviewer_email.error}</div>
                ) : (
                  ''
                )}
              </div>
            </div>
          </div>
          <div className='building-review-form__terms'>
            <p>
              {i18nTranslate(
                'I certify that this review is based on my own experience and is my genuine opinion of this property.'
              )}
            </p>
            <div className='building-review-form__terms-area'>
              <Checkbox id='buildingreview_terms' name='terms' checked={props.isTermsChecked} onClick={onChangeTerms}>
                {termsTextParts[0]}
                <a href={configGetCountry().termsUrl} target='_blank' rel='noreferrer' className='terms__checkbox-link'>
                  {i18nTranslate('Terms and Conditions')}
                </a>
                {termsTextParts[1]}
              </Checkbox>
            </div>
            {props.input.terms.error ? (
              <div className='building-review-form__error'>{props.input.terms.error}</div>
            ) : (
              ''
            )}
          </div>
          {props.action === 'send-error' ? (
            <div className='building-review-form__error building-review-form__error--general'>
              {i18nTranslate('An error happened on the server, please try again later.')}
            </div>
          ) : (
            ''
          )}
          <div className='building-review-form__submit'>
            <button
              className={`button button--size3 ${isSubmitDisabled() && 'building-review-form__disabled'}`}
              disabled={isSubmitDisabled()}
              onClick={props.onClickSubmit}
            >
              <div className={`button__text ${props.action === 'loading' ? 'button__content--hidden' : ''}`}>
                {i18nTranslate('Submit')}
              </div>
              <div className={`button__loader ${props.action === 'loading' ? '' : 'button__content--hidden'}`}>
                <div className='loader2' />
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export const BuildingReviewFormTemplate = preactLifecycleComponent(Markup);
